import {
  Mutation,
  MutationSubmitPushToCardTransferArgs,
  PushToCardTransferStatus,
  Query,
  SubmitPushToCardTransferErrorCode,
  SubmitPushToCardTransferResultTypenames,
  SubmitTransferInput,
  SubmitTransferResultTypenames,
  TransferRail,
  TransferReviewDetails,
} from '@varo/graphql-schema-types';
import { useNavigation, useRouter } from 'expo-router';
import type { NavigationAction } from 'expo-router/react-navigation';
import {
  accountTransferAttributes,
  formatErrorReason,
  PUSH_TO_CARD_TRANSFER_TYPE,
  type ReviewedTransfer,
  UNKNOWN_ERROR_REASON,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useSubmitTransferAnalytics, {
  SubmitAnalyticsSource,
} from 'features/feeBasedInstantTransfer/hooks/useSubmitTransferAnalytics';
import { useEffect, useRef, useState } from 'react';
import SubmitPushToCardTransfer from 'services/graphql/mutations/MoveMoney/SubmitPushToCardTransfer';
import SubmitTransfer from 'services/graphql/mutations/MoveMoney/SubmitTransfer';
import { FetchHomeTilesForIds } from 'services/graphql/queries/FetchHomeTilesV2';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { isWeb } from 'shared/constants/platform';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import usePreventBackNavigation from 'shared/hooks/usePreventBackNavigation/usePreventBackNavigation';
import { classifyError } from 'shared/utils/errors/classifyError';
import { getUuid } from 'shared/utils/getUuid/getUuid';

type ReviewedQuote = Pick<TransferReviewDetails, 'rail' | 'transferQuoteToken'>;

export type TransferRecovery = 'chooseDifferentAccount' | 'none';

type SubmitOutcome =
  | {
      kind: 'settled';
      rail: TransferRail;
      amount: string;
      transferSpeedMessage?: string;
    }
  | { kind: 'processing' }
  | { kind: 'rejected'; recovery: TransferRecovery; errorReason: string }
  | { kind: 'unknown' }
  | { kind: 'aborted' };

type SubmitStatus = 'idle' | 'submitting' | 'outcomeUnknown';

const PUSH_TO_CARD_ERROR_RECOVERIES: Record<
  SubmitPushToCardTransferErrorCode,
  TransferRecovery | 'outcomeUnknown'
> = {
  [SubmitPushToCardTransferErrorCode.Undefined]: 'none',
  [SubmitPushToCardTransferErrorCode.FeatureUnavailable]: 'none',
  [SubmitPushToCardTransferErrorCode.InvalidInput]: 'chooseDifferentAccount',
  [SubmitPushToCardTransferErrorCode.InvalidTransfer]: 'chooseDifferentAccount',
  [SubmitPushToCardTransferErrorCode.IdempotencyConflict]:
    'chooseDifferentAccount',
  [SubmitPushToCardTransferErrorCode.TransferFailed]: 'chooseDifferentAccount',
  [SubmitPushToCardTransferErrorCode.Unavailable]: 'outcomeUnknown',
};

const pushToCardErrorOutcome = (
  errorCode: SubmitPushToCardTransferErrorCode,
): SubmitOutcome => {
  const recovery = PUSH_TO_CARD_ERROR_RECOVERIES[errorCode] ?? 'none';
  return recovery === 'outcomeUnknown'
    ? { kind: 'unknown' }
    : {
        kind: 'rejected',
        recovery,
        errorReason: formatErrorReason(errorCode),
      };
};

const requestErrorOutcome = (error: unknown): SubmitOutcome => {
  const { type } = classifyError(error);
  switch (type) {
    case 'abort':
      return { kind: 'aborted' };
    case 'auth':
      return {
        kind: 'rejected',
        recovery: 'none',
        errorReason: UNKNOWN_ERROR_REASON,
      };
    case 'client':
      return {
        kind: 'rejected',
        recovery: 'chooseDifferentAccount',
        errorReason: UNKNOWN_ERROR_REASON,
      };
    case 'network':
    case 'timeout':
    case 'server':
    case 'graphql':
    case 'unknown':
      return { kind: 'unknown' };
  }
};

type SubmitVariables = SubmitTransferInput & { idempotencyId: string };

interface SubmitReviewedTransferOptions {
  analyticsSource: SubmitAnalyticsSource;
  flowName?: string;
}

export default function useSubmitReviewedTransfer(
  transfer: ReviewedTransfer | null,
  quote: ReviewedQuote | undefined,
  { analyticsSource, flowName }: SubmitReviewedTransferOptions,
) {
  const router = useRouter();
  const navigation = useNavigation();
  const [idempotencyId] = useState(getUuid);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const isSubmittingRef = useRef(false);
  const [, submitTransfer] = useCancellableMutation<
    Pick<Mutation, 'submitTransfer'>,
    SubmitVariables
  >(SubmitTransfer);
  const [, submitPushToCardTransfer] = useCancellableMutation<
    Pick<Mutation, 'submitPushToCardTransfer'>,
    MutationSubmitPushToCardTransferArgs
  >(SubmitPushToCardTransfer);
  const [refreshHomeTiles] =
    useCancellableQuery<Pick<Query, 'tiles'>>(FetchHomeTilesForIds);
  const [blockedBackAction, setBlockedBackAction] =
    useState<NavigationAction | null>(null);
  const {
    logOperationResult,
    logOperationError,
    logClientError,
    logTransferSuccessful,
  } = useSubmitTransferAnalytics(analyticsSource, flowName);

  const isOutcomePending = status !== 'idle';

  // An unknown outcome may still post, so leaving to re-review would resubmit under a new idempotency id.
  usePreventBackNavigation(isOutcomePending, (action) => {
    if (status === 'submitting') {
      setBlockedBackAction(action);
    }
  });

  useEffect(() => {
    if (!isWeb || !isOutcomePending) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOutcomePending]);

  const refreshTilesThen = async (
    tileIds: string[],
    outcome: SubmitOutcome,
  ): Promise<SubmitOutcome> => {
    if (tileIds.length === 0) {
      return outcome;
    }
    try {
      await refreshHomeTiles({ ids: tileIds, tileTypes: homeTileTypes });
    } catch (error) {
      if (error instanceof AbortError) {
        return { kind: 'aborted' };
      }
      logClientError('HomeTileRefreshFailed', error);
    }
    return outcome;
  };

  const submitAccountTransfer = async (
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ): Promise<SubmitOutcome> => {
    let response: Awaited<ReturnType<typeof submitTransfer>>;
    try {
      response = await submitTransfer({
        sourceAccountId: reviewed.source.id,
        destinationAccountId: reviewed.destination.id,
        amount: reviewed.amount,
        currency: reviewed.currency,
        idempotencyId,
      });
    } catch (error) {
      const outcome = requestErrorOutcome(error);
      if (outcome.kind !== 'aborted') {
        logClientError('SubmitTransferThrew', error);
      }
      return outcome;
    }
    if (response.error) {
      logOperationError('SubmitTransfer', response.error, response.extensions);
      return requestErrorOutcome(response.error);
    }
    const result = response.data?.submitTransfer;
    logOperationResult(
      'SubmitTransfer',
      result,
      result?.__typename ===
        SubmitTransferResultTypenames.SubmitTransferSuccess,
      response.extensions,
    );
    if (
      result?.__typename === SubmitTransferResultTypenames.SubmitTransferError
    ) {
      return {
        kind: 'rejected',
        recovery: 'chooseDifferentAccount',
        errorReason: formatErrorReason(result.code),
      };
    }
    if (
      result?.__typename !== SubmitTransferResultTypenames.SubmitTransferSuccess
    ) {
      return { kind: 'unknown' };
    }
    logTransferSuccessful(accountTransferAttributes(reviewed, rail, result));
    return refreshTilesThen(result.tileIdsToRefresh, {
      kind: 'settled',
      rail,
      amount: reviewed.amount,
      transferSpeedMessage: result.transferSpeedMessage ?? undefined,
    });
  };

  const submitPushToCard = async (
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ): Promise<SubmitOutcome> => {
    const transferQuoteToken = quote?.transferQuoteToken;
    if (!transferQuoteToken) {
      logClientError('TransferQuoteTokenMissing');
      return {
        kind: 'rejected',
        recovery: 'chooseDifferentAccount',
        errorReason: UNKNOWN_ERROR_REASON,
      };
    }
    let response: Awaited<ReturnType<typeof submitPushToCardTransfer>>;
    try {
      response = await submitPushToCardTransfer({
        idempotencyId,
        input: {
          source: reviewed.source,
          destination: reviewed.destination,
          principalAmount: {
            amount: reviewed.amount,
            currency: reviewed.currency,
          },
          transferQuoteToken,
        },
      });
    } catch (error) {
      const outcome = requestErrorOutcome(error);
      if (outcome.kind !== 'aborted') {
        logClientError('SubmitPushToCardThrew', error);
      }
      return outcome;
    }
    if (response.error) {
      logOperationError(
        'SubmitPushToCardTransfer',
        response.error,
        response.extensions,
      );
      return requestErrorOutcome(response.error);
    }
    const result = response.data?.submitPushToCardTransfer;
    logOperationResult(
      'SubmitPushToCardTransfer',
      result,
      result?.__typename ===
        SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferSuccess,
      response.extensions,
    );
    if (
      result?.__typename ===
      SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferError
    ) {
      return pushToCardErrorOutcome(result.errorCode);
    }
    if (
      result?.__typename !==
      SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferSuccess
    ) {
      return { kind: 'unknown' };
    }
    // UNDEFINED is an accepted but unclassified transfer, so only SETTLED can claim the money arrived.
    if (result.status !== PushToCardTransferStatus.Settled) {
      return refreshTilesThen(result.tileIdsToRefresh, { kind: 'processing' });
    }
    logTransferSuccessful({
      from_account_id: reviewed.source.id,
      to_account_id: reviewed.destination.id,
      transfer_amount: reviewed.amount,
      transfer_type: PUSH_TO_CARD_TRANSFER_TYPE,
    });
    return refreshTilesThen(result.tileIdsToRefresh, {
      kind: 'settled',
      rail,
      amount: reviewed.amount,
    });
  };

  const submitters: Partial<
    Record<
      TransferRail,
      (reviewed: ReviewedTransfer, rail: TransferRail) => Promise<SubmitOutcome>
    >
  > = {
    [TransferRail.Ach]: submitAccountTransfer,
    [TransferRail.Internal]: submitAccountTransfer,
    [TransferRail.PushToCard]: submitPushToCard,
  };

  const finishSubmission = (
    outcome: SubmitOutcome,
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ) => {
    switch (outcome.kind) {
      case 'settled':
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/instant-transfer-success',
          params: {
            rail: outcome.rail,
            amount: outcome.amount,
            transferSpeedMessage: outcome.transferSpeedMessage,
            flowName,
          },
        });
        return;
      case 'processing':
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/transfer-processing',
          params: { flowName },
        });
        return;
      case 'rejected':
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/instant-transfer-error',
          params: {
            rail,
            recovery: outcome.recovery,
            errorReason: outcome.errorReason,
            fromId: reviewed.source.id,
            amount: reviewed.amount,
            flowName,
          },
        });
        return;
      case 'unknown':
        isSubmittingRef.current = false;
        setBlockedBackAction(null);
        setStatus('outcomeUnknown');
        return;
      case 'aborted':
        return;
    }
  };

  const submit = async () => {
    const rail = quote?.rail;
    if (!transfer || !rail || isSubmittingRef.current) {
      return;
    }
    const submitter = submitters[rail];
    if (!submitter) {
      logClientError('UnsupportedTransferRail', rail);
      return;
    }
    isSubmittingRef.current = true;
    setStatus('submitting');
    // TODO(JT-163623): Fire the review submit press event; the control fires transferMoneySubmitted here.
    finishSubmission(await submitter(transfer, rail), transfer, rail);
  };

  const confirmLeave = () => {
    if (blockedBackAction) {
      navigation.dispatch(blockedBackAction);
    }
  };

  const cancelLeave = () => {
    setBlockedBackAction(null);
  };

  return {
    status,
    submit,
    isConfirmingLeave: blockedBackAction !== null,
    confirmLeave,
    cancelLeave,
  };
}
